import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, StatusDot, freshness, type StatusDotState } from '@diwata/ironvale'
import { DiwaAPI } from '@/api/client'
import { DomainsAPI } from '@/api/domains'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import { useUI } from '@/store/ui'
import { useSpringSelection } from '@/store/springSelection'
import './springs.css'

// Fallback names for a server that predates the catalog's display rows — the
// enriched /pipelines payload carries `display`/`gloss` and wins when present.
const DISPLAY: Record<string, string> = {
  'sec-press': 'SEC press releases',
  'fed-press': 'Federal Reserve press',
  'usaspending-awards': 'USAspending awards',
  'sam-opportunities': 'SAM.gov opportunities',
}

// Source CLASS → what it honestly means for the user's plan. Mirrors
// entitlements.CLASS_RANK + PLANS server-side: keeper=open, keeper_plus=curated,
// operator=credentialed, studio=paid, sovereign=custom.
export const CLASS_INFO: Record<string, { label: string; gloss: string }> = {
  open: { label: 'open', gloss: 'Free on every plan — a public feed that needs no key.' },
  curated: { label: 'curated', gloss: 'Keeper Plus and up — public-but-effortful sources Diwa runs and cleans for you.' },
  credentialed: { label: 'credentialed', gloss: 'Operator and up — runs on your own session or credentials, like the LinkedIn extension.' },
  paid: { label: 'paid', gloss: 'Studio and up — a metered paid API carried on the house key.' },
  custom: { label: 'custom', gloss: 'Sovereign — a spring built for one customer’s deployment.' },
}

// Lore health status → the StatusDot liveness vocabulary (mirrors _SPRING_STATE
// server-side, plus never_ran → neutral: an honest grey, never a fake pulse).
export const DOT_STATE: Record<string, StatusDotState> = {
  ok: 'live',
  late: 'idle',
  failing: 'error',
  rate_limited: 'error',
  never_ran: 'neutral',
}

export function humanSchedule(cron: string | null): string {
  if (!cron) return 'unscheduled'
  const m = cron.trim().split(/\s+/)
  if (m.length !== 5) return cron
  const [min, hour] = m
  if (hour === '*') return min === '0' ? 'hourly' : `hourly at :${min.padStart(2, '0')}`
  if (hour.startsWith('*/')) return `every ${hour.slice(2)} hours`
  if (/^\d+$/.test(hour)) return `daily ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`
  return cron
}

// The pipeline catalog: every source feeding the verse, with its breathing status.
// Tier 2 of pipelines-from-the-frontend — visibility and config, never authoring
// (that arrives with the agent protocol's propose-source gate).

interface Pipeline {
  id: string
  name: string
  source_type: string | null
  url: string | null
  schedule: string | null
  enabled: boolean
  owner_product: string | null
  critical: boolean
  health: { status?: string; last_run?: string; detail?: string; retry_hint_minutes?: number } | null
  /** Catalog garnish from the enriched /pipelines payload — absent on an older server. */
  display?: string
  gloss?: string
  class?: string
  tags?: string[]
  /** What this spring fills into the verse — entity + relation types. */
  produces?: { entities: string[]; relations: string[] }
}

/** "fills: opportunity · agency · signal" — the honest one-liner of what a spring
 *  lands. Renders nothing when a server predates the produces field or a spring
 *  declares no entities (a hand-authored planned row). */
function Produces({ produces }: { produces?: { entities: string[]; relations: string[] } }) {
  const entities = produces?.entities ?? []
  if (entities.length === 0) return null
  return (
    <span className="dw-spring-fills">
      <span className="dw-spring-fills-label">fills</span> {entities.join(' · ')}
    </span>
  )
}

/** A coming-soon row: a registry spring with no runtime source yet, or a hand-authored
 *  roadmap entry. No health, no dot — planned is planned. */
interface PlannedSpring {
  slug: string
  display: string
  gloss: string
  class: string
  note?: string | null
  tags?: string[]
  produces?: { entities: string[]; relations: string[] }
  planned: true
}

/** The source-class badge, glossed with what the class means for the user's plan. */
function ClassBadge({ cls }: { cls?: string }) {
  const info = cls ? CLASS_INFO[cls] : undefined
  if (!info) return null
  return (
    <HelpHint gloss={info.gloss}>
      <span className={`dw-spring-class dw-spring-class--${cls}`}>{info.label}</span>
    </HelpHint>
  )
}

// Families, not raw SAM codes — the backend translates (SDVOSB -> SDVOSBC+SDVOSBS,
// VOSB -> VSA+VSS, …). A human checks "SDVOSB"; the matcher speaks SAM.
interface RuleField {
  key: string
  label: string
  type: 'list' | 'int' | 'flag'
  help?: string
  max_items?: number
  min?: number
  max?: number
  default?: number
  options?: [string, string][]
}

interface RuleSchema {
  domain: string
  label: string
  description: string
  fields: RuleField[]
  reviewable?: boolean
}

/** One rules card per registered matcher — the form IS the schema (P13-T02).
 *  Load-before-edit discipline: rendering defaults as truth would let Save clobber
 *  real rules, so a failed load blocks the form. */
function RulesCard({ schema }: { schema: RuleSchema }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const [values, setValues] = useState<Record<string, string | string[]>>({})
  const [state, setState] = useState<'loading' | 'idle' | 'busy' | 'saved' | 'load_error' | 'save_error'>('loading')
  const [reviewState, setReviewState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const r = await api.get<{ rules: Record<string, unknown> }>(`/matcher/${schema.domain}`)
        const next: Record<string, string | string[]> = {}
        for (const f of schema.fields) {
          const raw = r.rules[f.key]
          if (f.type === 'int') next[f.key] = String(raw ?? f.default ?? 0)
          else if (f.type === 'flag') next[f.key] = raw ? 'true' : ''
          else if (f.options) next[f.key] = (raw as string[] | undefined) ?? []
          else next[f.key] = ((raw as string[] | undefined) ?? []).join(', ')
        }
        setValues(next)
        setState('idle')
      } catch {
        setState('load_error')
      }
    })()
  }, [api, schema])

  async function save() {
    setState('busy')
    try {
      const rules: Record<string, unknown> = {}
      for (const f of schema.fields) {
        const v = values[f.key]
        if (f.type === 'int') {
          const n = parseInt(String(v), 10) || f.default || 0
          const clamped = Math.min(f.max ?? n, Math.max(f.min ?? n, n))
          rules[f.key] = clamped
        } else if (f.type === 'flag') {
          rules[f.key] = v === 'true'
        } else if (f.options) {
          rules[f.key] = v as string[]
        } else {
          rules[f.key] = String(v).split(/[,\n]+/).map((c) => c.trim()).filter(Boolean)
        }
      }
      await api.put(`/matcher/${schema.domain}`, { rules })
      setState('saved')
      setTimeout(() => setState('idle'), 2000)
    } catch {
      setState('save_error')
    }
  }

  const configured = schema.fields.some((f) => {
    const v = values[f.key]
    return f.type !== 'int' && (Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '')
  })

  if (state === 'loading') return null
  if (!open) {
    return (
      <button type="button" className="dw-queue-row" style={{ marginBottom: 10 }} onClick={() => setOpen(true)}>
        <span className="dw-queue-glyph">●</span>
        <span className="dw-queue-title">
          Signal rules — {schema.label}
          <span className="dw-inbox-detail">
            {configured ? 'configured — click to review' : 'not configured — this matcher runs on defaults or stays silent'}
          </span>
        </span>
      </button>
    )
  }
  if (state === 'load_error') {
    return (
      <Card style={{ marginBottom: 14 }}>
        <div className="dw-rail-section-title">Signal rules — {schema.label}</div>
        <p className="dw-error">Couldn't load your rules — editing now could overwrite them. <button className="dw-link" style={{ background: 'none', border: 'none' }} onClick={() => window.location.reload()}>Retry</button></p>
      </Card>
    )
  }
  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div className="dw-rail-section-title">Signal rules — {schema.label}</div>
        <button type="button" className="dw-back-link" style={{ padding: 0 }} onClick={() => setOpen(false)}>collapse</button>
      </div>
      <p className="dw-inbox-detail" style={{ marginBottom: 10 }}>{schema.description}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        {schema.fields.map((f) => {
          if (f.type === 'int') {
            return (
              <label key={f.key} style={{ fontSize: 'var(--ae-font-size-xs)' }}>
                {f.label}
                <input className="dw-input" inputMode="numeric" style={{ marginTop: 4, width: 90 }}
                       value={String(values[f.key] ?? '')}
                       onChange={(e) => setValues((st) => ({ ...st, [f.key]: e.target.value }))} />
                {f.help && <span className="dw-inbox-detail" style={{ display: 'block', marginTop: 2 }}>{f.help}</span>}
              </label>
            )
          }
          if (f.type === 'flag') {
            return (
              <label key={f.key} style={{ fontSize: 'var(--ae-font-size-xs)', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={values[f.key] === 'true'}
                       onChange={(e) => setValues((st) => ({ ...st, [f.key]: e.target.checked ? 'true' : '' }))} />
                {f.label}
                {f.help && <span className="dw-inbox-detail">{f.help}</span>}
              </label>
            )
          }
          if (f.options) {
            const chosen = (values[f.key] as string[]) ?? []
            return (
              <div key={f.key} style={{ fontSize: 'var(--ae-font-size-xs)' }}>
                {f.label}
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4 }}>
                  {f.options.map(([code, label]) => (
                    <label key={code} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      <input type="checkbox" checked={chosen.includes(code)}
                             onChange={(e) => setValues((st) => ({
                               ...st,
                               [f.key]: e.target.checked ? [...chosen, code] : chosen.filter((c) => c !== code),
                             }))} />
                      {label}
                    </label>
                  ))}
                </div>
                {f.help && <span className="dw-inbox-detail">{f.help}</span>}
              </div>
            )
          }
          return (
            <label key={f.key} style={{ fontSize: 'var(--ae-font-size-xs)' }}>
              {f.label} (comma-separated)
              <input className="dw-input" style={{ marginTop: 4 }} value={String(values[f.key] ?? '')}
                     onChange={(e) => setValues((st) => ({ ...st, [f.key]: e.target.value }))} />
              {f.help && <span className="dw-inbox-detail" style={{ display: 'block', marginTop: 2 }}>{f.help}</span>}
            </label>
          )
        })}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="sm" variant="primary" disabled={state === 'busy'} onClick={save}>
            {state === 'busy' ? 'Saving…' : state === 'saved' ? 'Saved ✓' : 'Save rules'}
          </Button>
          {schema.reviewable && (
          <Button size="sm" variant="ghost" disabled={reviewState === 'busy' || reviewState === 'done'}
                  onClick={async () => {
                    setReviewState('busy')
                    try {
                      await api.post('/missions/rule-review', { domain: schema.domain })
                      setReviewState('done')
                    } catch {
                      setReviewState('error')
                    }
                  }}>
            {reviewState === 'busy' ? 'Summoning…' : reviewState === 'done' ? 'Review mission created ✦' : '✦ Have Tala review these rules'}
          </Button>
          )}
          {reviewState === 'done' && <a className="dw-link" href="/missions">open Missions →</a>}
          {state === 'save_error' && (
            <span className="dw-error">Couldn't save — check the caps and try again.</span>
          )}
          {reviewState === 'error' && <span className="dw-error">Couldn't create the review — retry.</span>}
        </div>
      </div>
    </Card>
  )
}

interface DiscoverExample {
  title: string | null
  agency: string | null
  source_url: string | null
  est_value: number | string | null  // the wire serializes Decimal as a string
  response_due: string | null
  clearance_required: string | null
}

function money(v: number | string | null): string | null {
  if (v == null) return null
  const n = Number(v)
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : null
}

interface DiscoverResult {
  sample: number
  codes: { naics: string; count: number; examples: DiscoverExample[] }[]
  observed_set_asides: Record<string, number>
  easy_mode: { rules: Record<string, unknown>; active_matches: number } | null
}

/** Plain-language onboarding (P14-T04): describe the work, the lake answers with
 *  the NAICS codes real opportunities carry. Every number shown is a lake row —
 *  nothing here is invented. */
function FindMyCodes({ onRulesApplied }: { onRulesApplied: () => void }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle')
  const [result, setResult] = useState<DiscoverResult | null>(null)
  const [applyState, setApplyState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle')
  const [draftState, setDraftState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle')

  async function search() {
    setState('busy')
    setApplyState('idle')
    setDraftState('idle')
    setResult(null)  // stale results under a new search (or its error) would mislead
    try {
      setResult(await api.post<DiscoverResult>('/matcher/govcon/discover', { text }))
      setState('idle')
    } catch {
      setState('error')
    }
  }

  async function applyEasyMode() {
    if (!result?.easy_mode) return
    setApplyState('busy')
    try {
      // merge over what's already saved — easy mode must not clobber other rules
      const current = await api.get<{ rules: Record<string, unknown> }>('/matcher/govcon')
      await api.put('/matcher/govcon', { rules: { ...current.rules, ...result.easy_mode.rules } })
      setApplyState('done')
      onRulesApplied()
    } catch {
      setApplyState('error')
    }
  }

  async function askFamiliar() {
    setDraftState('busy')
    try {
      await api.post('/missions/rule-review', { domain: 'govcon', intent: text })
      setDraftState('done')
    } catch {
      setDraftState('error')
    }
  }

  if (!open) {
    return (
      <button type="button" className="dw-queue-row" style={{ marginBottom: 10 }} onClick={() => setOpen(true)}>
        <span className="dw-queue-glyph">◈</span>
        <span className="dw-queue-title">
          Find my codes
          <span className="dw-inbox-detail">don't know your NAICS? describe the work — the lake answers with codes and live examples</span>
        </span>
      </button>
    )
  }

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div className="dw-rail-section-title">Find my codes</div>
        <button type="button" className="dw-back-link" style={{ padding: 0 }} onClick={() => setOpen(false)}>collapse</button>
      </div>
      <p className="dw-inbox-detail" style={{ marginBottom: 10 }}>
        Describe the work you do in plain words. The answer comes from the lake: which NAICS codes real
        matching opportunities carry, with live examples.
      </p>
      <textarea
        className="dw-input"
        rows={2}
        placeholder="e.g. janitorial and grounds maintenance for federal buildings"
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{ width: '100%', resize: 'vertical', marginBottom: 8 }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="sm" variant="primary" disabled={state === 'busy' || text.trim().length < 3} onClick={search}>
          {state === 'busy' ? 'Asking the lake…' : 'Search the lake'}
        </Button>
        {state === 'error' && <span className="dw-error">The lake didn't answer — try again in a moment.</span>}
      </div>

      {result && result.codes.length === 0 && (
        <div style={{ marginTop: 10, display: 'grid', gap: 8, justifyItems: 'start' }}>
          <p className="dw-empty" style={{ margin: 0 }}>
            No lake opportunities matched those words yet — try different words, or hand it to Tala.
          </p>
          <Button size="sm" variant="ghost" disabled={draftState === 'busy' || draftState === 'done'} onClick={askFamiliar}>
            {draftState === 'busy' ? 'Summoning…' : draftState === 'done' ? 'Draft mission created ✦' : '✦ Have Tala draft rules from this'}
          </Button>
          {draftState === 'done' && <a className="dw-link" href="/missions">open Missions →</a>}
          {draftState === 'error' && <span className="dw-error">Couldn't create the mission — retry.</span>}
        </div>
      )}

      {result && result.codes.length > 0 && (
        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
          {result.codes.slice(0, 5).map((c) => (
            <div key={c.naics}>
              <div style={{ fontSize: 'calc(13px * var(--ae-text-scale, 1))' }}>
                <strong>NAICS {c.naics}</strong>
                <span className="dw-inbox-detail" style={{ marginLeft: 8 }}>
                  {c.count} matching {c.count === 1 ? 'opportunity' : 'opportunities'} in the lake
                </span>
              </div>
              {c.examples.map((ex, i) => (
                <div key={`${c.naics}:${i}`} className="dw-inbox-detail" style={{ marginLeft: 12 }}>
                  {ex.source_url ? (
                    <a className="dw-link" href={ex.source_url} target="_blank" rel="noreferrer">{ex.title}</a>
                  ) : (
                    ex.title
                  )}
                  {money(ex.est_value) && ` · ${money(ex.est_value)}`}
                  {ex.clearance_required && ` · requires ${ex.clearance_required} clearance`}
                </div>
              ))}
            </div>
          ))}
          {Object.keys(result.observed_set_asides).length > 0 && (
            <p className="dw-inbox-detail" style={{ margin: 0 }}>
              Competition mix seen:{' '}
              {Object.entries(result.observed_set_asides).map(([k, n]) => `${k} ×${n}`).join(', ')} — tick the
              set-aside families you're certified for in the rules card; only you know your eligibility.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {result.easy_mode && (
              <Button size="sm" variant="primary" disabled={applyState === 'busy' || applyState === 'done'} onClick={applyEasyMode}>
                {applyState === 'busy'
                  ? 'Applying…'
                  : applyState === 'done'
                    ? 'Easy mode applied ✓'
                    : `Apply easy mode — ${result.easy_mode.active_matches} live ${result.easy_mode.active_matches === 1 ? 'match' : 'matches'} in this sample`}
              </Button>
            )}
            <Button size="sm" variant="ghost" disabled={draftState === 'busy' || draftState === 'done'} onClick={askFamiliar}>
              {draftState === 'busy' ? 'Summoning…' : draftState === 'done' ? 'Draft mission created ✦' : '✦ Have Tala draft rules from this'}
            </Button>
            {draftState === 'done' && <a className="dw-link" href="/missions">open Missions →</a>}
            {applyState === 'error' && <span className="dw-error">Couldn't save the preset — retry.</span>}
            {draftState === 'error' && <span className="dw-error">Couldn't create the mission — retry.</span>}
          </div>
          {result.easy_mode && applyState !== 'done' && (
            <p className="dw-inbox-detail" style={{ margin: 0 }}>
              Easy mode: your top codes, value under $250k (the simplified-acquisition threshold), and skip
              rows whose documents demand a clearance. It merges into the rules card below — nothing else changes.
            </p>
          )}
        </div>
      )}
    </Card>
  )
}

/** Signal rules, relocated (founder walk 3). The matcher forms used to be a wall
 *  at the TOP of Springs — one card per registered matcher, dominating the page
 *  before you reached a single spring. They now live in ONE collapsed disclosure
 *  below the springs + planned shelf, one card per domain you actually hold. Same
 *  GET/PUT /matcher endpoints, same forms — just reachable from here, keyed to the
 *  relevant domain, instead of drowning the main view. The rail spring detail is
 *  owned elsewhere (Layout), so this page-level section is the connected home. */
function SignalRulesSection({ domainSlugs }: { domainSlugs: Set<string> }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const [open, setOpen] = useState(false)
  // Bumped after easy-mode applies to remount ONLY the govcon card — remounting the
  // rest would discard unsaved edits in any open card.
  const [rulesEpoch, setRulesEpoch] = useState(0)
  const { data } = useQuery({
    queryKey: ['matcher-schemas'],
    queryFn: () => api.get<{ schemas: RuleSchema[] }>('/matcher/schemas'),
  })
  // Only cards for regions the user actually has: no govcon NAICS card for a
  // media/crm user, even though /matcher/schemas still lists every static matcher.
  const visible = (data?.schemas ?? []).filter((sc) => domainSlugs.has(sc.domain))
  // No matcher for any domain the user holds — render nothing rather than tease an
  // empty section.
  if (visible.length === 0) return null

  return (
    <div style={{ marginTop: 24 }}>
      {/* The HelpHint renders its own <button>, so it sits BESIDE the toggle in a
          shared bar — never inside it (invalid button-in-button; the "?" used to
          toggle the section). The bar carries the old toggle's frame styling. */}
      <div className="dw-signal-rules-bar">
        <button
          type="button"
          className="dw-signal-rules-toggle"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="dw-signal-rules-caret" aria-hidden>{open ? '▾' : '▸'}</span>
          <span className="dw-field-label" style={{ margin: 0 }}>Signal rules</span>
        </button>
        <HelpHint term="matcher" />
        <span className="dw-inbox-detail">
          {visible.length} {visible.length === 1 ? 'domain' : 'domains'} — what each spring promotes into signals
        </span>
      </div>
      {open && (
        <div style={{ marginTop: 12 }}>
          {domainSlugs.has('govcon') && (
            <FindMyCodes onRulesApplied={() => setRulesEpoch((n) => n + 1)} />
          )}
          {visible.map((sc) => (
            <RulesCard key={sc.domain === 'govcon' ? `govcon:${rulesEpoch}` : sc.domain} schema={sc} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Request a new spring: URL + intent — now a source-draft MISSION (P7-T03): the
 *  Familiar drafts the manifest, dry-runs it once, and proposes at your gate. */
function RequestSource() {
  const { diwaUrl } = useUI()
  const missionApi = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [intent, setIntent] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'error'>('idle')

  if (!open) {
    return (
      <button type="button" className="dw-queue-row" style={{ marginBottom: 10 }} onClick={() => setOpen(true)}>
        <span className="dw-queue-glyph">＋</span>
        <span className="dw-queue-title">
          Request a source
          <span className="dw-inbox-detail">a URL or feed you want flowing into the lake</span>
        </span>
      </button>
    )
  }
  async function send() {
    if (!url.trim() || state === 'busy') return
    setState('busy')
    try {
      await missionApi.post('/missions/source-draft', { url: url.trim(), intent: intent.trim() })
      setState('sent')
      setTimeout(() => { setOpen(false); setUrl(''); setIntent(''); setState('idle') }, 2200)
    } catch {
      setState('error')
    }
  }
  return (
    <Card style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div className="dw-rail-section-title">Request a source</div>
        <button type="button" className="dw-back-link" style={{ padding: 0 }} onClick={() => setOpen(false)}>collapse</button>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        <input className="dw-input" placeholder="https://example.gov/feed.rss" value={url} onChange={(e) => setUrl(e.target.value)} />
        <textarea className="dw-input dw-textarea" rows={2} placeholder="What do you want from it? (e.g. new grant notices for NAICS 541512)" value={intent} onChange={(e) => setIntent(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button size="sm" variant="primary" disabled={state === 'busy' || !url.trim()} onClick={send}>
            {state === 'busy' ? 'Filing…' : state === 'sent' ? 'Filed ✓' : 'Request it'}
          </Button>
          {state === 'sent' && <span className="dw-inbox-detail">Filed to the desk — it becomes a spring after review.</span>}
          {state === 'error' && <span className="dw-error">Could not file — try again.</span>}
        </div>
      </div>
    </Card>
  )
}

/** The planned-shelf request (Springs round 2, item 8): an INLINE expandable form,
 *  not a mailto. Files a lightweight source request to the desk's review — distinct
 *  from the top-of-page RequestSource, which drafts a full source MISSION. */
function RequestPlannedSource() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [detail, setDetail] = useState('')
  const [url, setUrl] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'error'>('idle')

  async function send() {
    if (!name.trim() || state === 'busy') return
    setState('busy')
    try {
      await api.post('/waitlist/source-request', {
        name: name.trim(),
        detail: detail.trim() || null,
        url: url.trim() || null,
      })
      setState('sent')
    } catch {
      setState('error')
    }
  }

  if (state === 'sent') {
    return (
      <div className="dw-inbox-detail" style={{ marginTop: 10 }}>
        Request received — it joins the planned shelf review. Thank you.
      </div>
    )
  }

  if (!open) {
    return (
      <div className="dw-inbox-detail" style={{ marginTop: 8 }}>
        Want one of these sooner — or a source we haven't thought of?{' '}
        <button type="button" className="dw-inline-link" style={{ font: 'inherit' }} onClick={() => setOpen(true)}>
          Request a source →
        </button>
      </div>
    )
  }

  return (
    <Card style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div className="dw-rail-section-title">Request a source</div>
        <button type="button" className="dw-back-link" style={{ padding: 0 }} onClick={() => setOpen(false)}>collapse</button>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        <label style={{ fontSize: 'var(--ae-font-size-xs)' }}>
          What source?
          <input className="dw-input" style={{ marginTop: 4 }} placeholder="e.g. EDGAR full-text search"
                 value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label style={{ fontSize: 'var(--ae-font-size-xs)' }}>
          Why — what should it fill?
          <textarea className="dw-input dw-textarea" rows={2} style={{ marginTop: 4 }}
                    placeholder="e.g. new 8-K filings for the companies I watch"
                    value={detail} onChange={(e) => setDetail(e.target.value)} />
        </label>
        <label style={{ fontSize: 'var(--ae-font-size-xs)' }}>
          URL (optional)
          <input className="dw-input" style={{ marginTop: 4 }} placeholder="https://…"
                 value={url} onChange={(e) => setUrl(e.target.value)} />
        </label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button size="sm" variant="primary" disabled={state === 'busy' || !name.trim()} onClick={send}>
            {state === 'busy' ? 'Filing…' : 'Request it'}
          </Button>
          {state === 'error' && <span className="dw-error">Could not file — try again.</span>}
        </div>
      </div>
    </Card>
  )
}

export function Pipelines() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const domainsApi = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const selectSpring = useSpringSelection((st) => st.select)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api.get<{ pipelines: Pipeline[]; planned?: PlannedSpring[] }>('/pipelines'),
  })
  // Scope the signal-rules section (incl. the govcon NAICS "Find my codes" helper)
  // to users who actually hold each region — nothing federal for a media user.
  const { data: domainsData } = useQuery({ queryKey: ['domains'], queryFn: () => domainsApi.list() })
  const domainSlugs = useMemo(
    () => new Set((domainsData?.domains ?? []).map((d) => d.slug)),
    [domainsData],
  )

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">
            Springs <HelpHint term="spring" /> <FlowDiagram />
          </div>
          <div className="dw-page-subtitle">
            What feeds the verse — a domain is alive only while its springs flow.
          </div>
        </div>
      </div>
      <RequestSource />
      {isLoading && <p className="dw-empty">Loading…</p>}
      {isError && <p className="dw-error">Couldn't reach the lake's registry.</p>}
      {data && data.pipelines.length === 0 && (
        <div className="dw-rail-empty">No sources registered yet.</div>
      )}
      <div style={{ display: 'grid', gap: 10 }}>
        {(data?.pipelines ?? []).map((p) => {
          const status = p.health?.status ?? 'never_ran'
          const display = p.display ?? DISPLAY[p.name] ?? p.name
          return (
            <button
              type="button"
              key={p.id}
              className="dw-spring-card"
              onClick={() =>
                selectSpring({
                  id: p.id, name: p.name, display,
                  source_type: p.source_type, url: p.url, schedule: p.schedule,
                  scheduleHuman: humanSchedule(p.schedule), enabled: p.enabled,
                  owner_product: p.owner_product, critical: p.critical, health: p.health,
                  gloss: p.gloss, sourceClass: p.class, tags: p.tags, produces: p.produces,
                })
              }>
              <span className="dw-spring-card-head">
                <StatusDot
                  state={DOT_STATE[status] ?? 'neutral'}
                  size="sm"
                  label={`${display} — ${status.replace('_', ' ')}`}
                />
                <strong className="dw-spring-name">{display}</strong>
                <span className="dw-spring-slug">{p.name}</span>
                {p.critical && <span className="dw-inbox-detail">· critical</span>}
                {!p.enabled && <span className="dw-inbox-detail">· disabled</span>}
                <span className="dw-spring-head-right">
                  <ClassBadge cls={p.class} />
                  <span className="dw-inbox-detail">
                    {status === 'rate_limited'
                      ? `throttled by the source — resumes in ~${Math.max(1, Math.round((p.health?.retry_hint_minutes ?? 60) / 60))}h`
                      : humanSchedule(p.schedule)}
                  </span>
                </span>
              </span>
              {p.gloss && <span className="dw-spring-gloss">{p.gloss}</span>}
              <Produces produces={p.produces} />
              {(p.tags?.length ?? 0) > 0 && (
                <span className="dw-spring-tags">
                  {p.tags!.map((t) => (
                    <span key={t} className="dw-chip">{t}</span>
                  ))}
                </span>
              )}
              <span className="dw-spring-meta dw-inbox-detail">
                {p.source_type ?? 'source'}
                {p.owner_product && ` · ${p.owner_product}`}
                {p.health?.last_run && (
                  <>
                    {' · '}
                    <HelpHint gloss="When this spring last pulled from its source into the lake.">
                      <span>pulled {freshness(p.health.last_run)}</span>
                    </HelpHint>
                  </>
                )}
                {p.health?.detail && ` · ${p.health.detail}`}
              </span>
              {p.url && (
                <a
                  className="dw-spring-meta dw-spring-url"
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  // the card is itself clickable (it opens the rail) — stop the
                  // link's click from bubbling up and also selecting the spring.
                  onClick={(e) => e.stopPropagation()}
                  style={{ wordBreak: 'break-all' }}
                >
                  {p.url} ↗
                </a>
              )}
            </button>
          )
        })}
      </div>
      {(data?.planned?.length ?? 0) > 0 && (
        <div style={{ marginTop: 22 }}>
          <div className="dw-field-label" style={{ marginBottom: 8 }}>
            Planned springs{' '}
            <HelpHint gloss="Sources in the catalog that don't flow yet — each shows the tier it will arrive on." />
          </div>
          <div className="dw-spring-planned-grid">
            {data!.planned!.map((s) => (
              <div key={s.slug} className="dw-spring-card dw-spring-card--planned">
                <span className="dw-spring-card-head dw-spring-card-head--stacked">
                  <span className="dw-spring-titleblock">
                    <strong className="dw-spring-name">{s.display}</strong>
                    <span className="dw-spring-slug">{s.slug}</span>
                  </span>
                  <span className="dw-spring-head-right">
                    <ClassBadge cls={s.class} />
                  </span>
                </span>
                {s.gloss && <span className="dw-spring-gloss">{s.gloss}</span>}
                <Produces produces={s.produces} />
                {s.note && <span className="dw-spring-note">{s.note}</span>}
              </div>
            ))}
          </div>
          <RequestPlannedSource />
        </div>
      )}
      {/* Signal rules folded off the top of the page — reachable here, keyed per domain. */}
      <SignalRulesSection domainSlugs={domainSlugs} />
    </div>
  )
}
